/**
 * Story 单集拍摄（B 域）本人动作 SDK。
 *
 * 唯一函数路径：报价、提交、任务读取。只包装已稳定的 B HTTP 合同；
 * 不替用户改价、不自动重试、不静默替换首免，也不触发供应商/账本终态——
 * 是否真的扣费、受理、生成由服务端按冻结来源与报价决定。
 */

const PREFIX = '/api/visual-game/story-film'

/**
 * 一次「用户确认提交」的唯一幂等键。
 *
 * 规则（唯一安全语义）：
 * - 同一次点击/超时后不确定是否受理：必须复用同一个 key，服务端只回放同一受理，不重复扣费；
 * - 报价变化：不换 key，页面展示新报价并重新确认；服务端返回 QUOTE_STALE，不能静默换价；
 * - 确认失败/退款后要重拍：必须换新 key，且服务端 STORY_FILM_RETAKE_POLICY_APPROVED 未批准时
 *   以 RETAKE_UNAPPROVED fail-closed，不扣费、不建任务；
 * - 禁止用 episode/snapshot/plan_digest/points 派生 key，否则旧失败单会被当成 replay。
 */
export function createStoryFilmRequestKey() {
  const uuid = globalThis.crypto?.randomUUID?.()
  if (typeof uuid !== 'string' || uuid === '') throw new Error('Story film request key requires crypto.randomUUID')
  return `sfreq:${uuid}`
}

/**
 * @param {{ request: Function }} transport
 * @param {{ prefix?: string }} [options]
 */
export function createStoryFilmProductionApi(transport, { prefix = PREFIX } = {}) {
  if (!transport || typeof transport.request !== 'function') {
    throw new Error('createStoryFilmProductionApi: transport.request is required')
  }
  return {
    /**
     * 按本人单集冻结来源幂等排队 Agent 准备；不受理拍摄，也不扣费。
     * @param {{ episode_id: string }} body
     */
    prepareAgentEpisode({ episode_id, snapshot_id, request_id, previous_preparation_id } = {}) {
      return transport.request('POST', `${prefix}/agent-preparations`, {
        body: { episode_id: requireString(episode_id, 'episode_id'), snapshot_id, request_id, previous_preparation_id },
        parse: parseAgentPreparationResponse,
      })
    },
    /**
     * 单集拍摄状态：每集固定积分与最近一次拍摄请求（preparing / accepted / refunded）。
     * @param {{ episode_id: string }} params
     */
    getEpisodeShoot({ episode_id } = {}) {
      return transport.request('GET', `${prefix}/episodes/${encodeURIComponent(requireString(episode_id, 'episode_id'))}/shoot`, { parse: parseShootStatus })
    },
    /**
     * 单集拍摄：点击即按固定积分扣费并记拍摄请求；同一 request_key 重放只返回原请求。
     * @param {{ episode_id: string, request_key: string }} body
     */
    shootEpisode({ episode_id, request_key } = {}) {
      return transport.request('POST', `${prefix}/episodes/${encodeURIComponent(requireString(episode_id, 'episode_id'))}/shoot`, {
        body: { request_key: requireString(request_key, 'request_key') },
        parse: value => parseShootRequest(value?.request),
      })
    },
    /**
     * 拍前报价：镜数/来源只由服务端冻结来源与逐镜计划决定。
     * @param {{ episode_id: string }} params
     */
    quoteEpisode({ episode_id, snapshot_id = null, preparation_id = null } = {}) {
      const params = new URLSearchParams({ episode_id: requireString(episode_id, 'episode_id') })
      if (snapshot_id !== null) params.set('snapshot_id', requireString(snapshot_id, 'snapshot_id'))
      if (preparation_id !== null) params.set('preparation_id', String(requireInteger(preparation_id, 'preparation_id')))
      return transport.request('GET', `${prefix}/quote?${params.toString()}`, { parse: parseQuoteResponse })
    },
    /**
     * 手动受理。expected_quote 原样回传上次报价；价格/来源/计划任何变化由服务端返回
     * QUOTE_STALE，本方法不自动改用新价。手动受理不会静默占用首免资格。
     * @param {{ episode_id: string, request_key: string, expected_quote: object }} body
     */
    submitEpisode({ episode_id, request_key, expected_quote } = {}) {
      return transport.request('POST', `${prefix}/submissions`, {
        body: {
          episode_id: requireString(episode_id, 'episode_id'),
          request_key: requireString(request_key, 'request_key'),
          expected_quote: expectedQuoteOf(expected_quote),
        },
        parse: parseSubmitResult,
      })
    },
    /**
     * 本人任务列表（只读；不含对象键/凭证）。
     * @param {{ limit?: number|null, cursor?: string|number|null }} [query]
     */
    listTasks({ limit = null, cursor = null } = {}) {
      const params = new URLSearchParams()
      if (limit !== null && limit !== undefined) params.set('limit', String(limit))
      if (cursor !== null && cursor !== undefined && cursor !== '') params.set('cursor', String(cursor))
      const query = params.toString()
      return transport.request('GET', `${prefix}/tasks${query ? `?${query}` : ''}`, { parse: parseTaskList })
    },
    /** 本人单集任务详情；他人/不存在返回 NOT_FOUND，不泄露存在性。 */
    getTask({ submission_id } = {}) {
      return transport.request('GET', `${prefix}/tasks/${encodeURIComponent(requireNumberString(submission_id, 'submission_id'))}`, {
        parse: parseTaskDetail,
      })
    },
  }
}

function parseAgentPreparationResponse(value) {
  if (!isObject(value?.preparation) || typeof value.preparation.reused !== 'boolean') throw new Error('Story film Agent preparation response is invalid')
  const preparation = value.preparation
  return {
    preparation_id: requireInteger(preparation.preparation_id, 'preparation_id'),
    status: requireString(preparation.status, 'status'),
    source_version: requireString(preparation.source_version, 'source_version'),
    reused: preparation.reused,
  }
}

function expectedQuoteOf(value) {
  if (!isObject(value)) throw new Error('Story film expected_quote is required')
  return {
    points: requireInteger(value.points, 'expected_quote.points'),
    cost_model_version: requireString(value.cost_model_version, 'expected_quote.cost_model_version'),
    source_version: requireString(value.source_version, 'expected_quote.source_version'),
    snapshot_id: requireString(value.snapshot_id, 'expected_quote.snapshot_id'),
    preparation_id: value.preparation_id == null ? undefined : requireInteger(value.preparation_id, 'expected_quote.preparation_id'),
    cut_policy_version: value.cut_policy_version === undefined || value.cut_policy_version === null ? undefined : String(value.cut_policy_version),
    plan_digest: requireString(value.plan_digest, 'expected_quote.plan_digest'),
    execution_pack_digest: value.execution_pack_digest === undefined || value.execution_pack_digest === null ? undefined : requireString(value.execution_pack_digest, 'expected_quote.execution_pack_digest'),
    selection_digest: value.selection_digest === undefined || value.selection_digest === null ? undefined : requireString(value.selection_digest, 'expected_quote.selection_digest'),
    selection_version: value.selection_version === undefined || value.selection_version === null ? undefined : String(value.selection_version),
  }
}

const SHOOT_STATUSES = new Set(['preparing', 'accepted', 'refunded'])

function parseShootRequest(value) {
  if (!isObject(value) || !SHOOT_STATUSES.has(value.status)) throw new Error('Story film shoot request is invalid')
  return {
    request_key: requireString(value.request_key, 'request.request_key'),
    episode_id: requireString(value.episode_id, 'request.episode_id'),
    points: requireInteger(value.points, 'request.points'),
    status: value.status,
    submission_id: value.submission_id === null ? null : requireInteger(value.submission_id, 'request.submission_id'),
    failure_code: value.failure_code === null ? null : requireString(value.failure_code, 'request.failure_code'),
    created_at: requireString(value.created_at, 'request.created_at'),
  }
}

function parseShootStatus(value) {
  if (!isObject(value)) throw new Error('Story film shoot status is invalid')
  return { points: requireInteger(value.points, 'points'), first_free_pending: value.first_free_pending === true, previous_legacy: value.previous_legacy === true, request: value.request === null ? null : parseShootRequest(value.request) }
}

function parseQuoteResponse(value) {
  if (!isObject(value) || !isObject(value.quote)) throw new Error('Story film quote response must contain quote')
  const quote = value.quote
  if (!isObject(quote.storyboard)) throw new Error('Story film quote.storyboard is invalid')
  return {
    points: requireInteger(quote.points, 'quote.points'),
    currency: requireString(quote.currency, 'quote.currency'),
    cost_model_version: requireString(quote.cost_model_version, 'quote.cost_model_version'),
    cost_micros: requireInteger(quote.cost_micros, 'quote.cost_micros'),
    coefficient_ppm: optionalInteger(quote.coefficient_ppm),
    cost_source: optionalString(quote.cost_source),
    rounding: optionalString(quote.rounding),
    quoted_at: optionalString(quote.quoted_at),
    segments: Array.isArray(quote.segments) ? quote.segments : [],
    episode_id: requireString(quote.episode_id, 'quote.episode_id'),
    source_version: requireString(quote.source_version, 'quote.source_version'),
    snapshot_id: requireString(quote.snapshot_id, 'quote.snapshot_id'),
    preparation_id: optionalInteger(quote.preparation_id),
    cut_policy_version: optionalString(quote.cut_policy_version),
    execution_pack_digest: optionalString(quote.execution_pack_digest),
    can_accept: optionalBoolean(quote.can_accept),
    can_call_h3: optionalBoolean(quote.can_call_h3),
    generation_authorized: optionalBoolean(quote.generation_authorized),
    acceptance: quote.acceptance === undefined || quote.acceptance === null
      ? null
      : {
          acceptable: optionalBoolean(quote.acceptance.acceptable),
          complete_shots: optionalInteger(quote.acceptance.complete_shots),
          total_shots: optionalInteger(quote.acceptance.total_shots),
          vendor_callable: optionalBoolean(quote.acceptance.vendor_callable),
          blockers: Array.isArray(quote.acceptance.blockers) ? quote.acceptance.blockers.map(String) : [],
        },
    readiness: quote.readiness === undefined || quote.readiness === null
      ? null
      : {
          sendable: quote.readiness.sendable === true,
          sendable_shots: requireInteger(quote.readiness.sendable_shots, 'quote.readiness.sendable_shots'),
          total_shots: requireInteger(quote.readiness.total_shots, 'quote.readiness.total_shots'),
          blockers: Array.isArray(quote.readiness.blockers) ? quote.readiness.blockers.map(String) : [],
        },
    selection: quote.selection === undefined || quote.selection === null
      ? null
      : {
          selection_version: optionalString(quote.selection.selection_version),
          selection_digest: requireString(quote.selection.selection_digest, 'quote.selection.selection_digest'),
          status: optionalString(quote.selection.status),
          segment_count: optionalInteger(quote.selection.segment_count),
          char_count: optionalInteger(quote.selection.char_count),
          ending_kind: optionalString(quote.selection.ending_kind),
        },
    storyboard: {
      plan_version: requireString(quote.storyboard.plan_version, 'quote.storyboard.plan_version'),
      plan_digest: requireString(quote.storyboard.plan_digest, 'quote.storyboard.plan_digest'),
      shots: requireInteger(quote.storyboard.shots, 'quote.storyboard.shots'),
      total_duration_ms: requireInteger(quote.storyboard.total_duration_ms, 'quote.storyboard.total_duration_ms'),
      total_frames: optionalInteger(quote.storyboard.total_frames),
      h3_total_frames: optionalInteger(quote.storyboard.h3_total_frames),
      h3_generation_total_ms: optionalInteger(quote.storyboard.h3_generation_total_ms),
      h3_total_max_frames: optionalInteger(quote.storyboard.h3_total_max_frames),
      h3_total_max_ms: optionalInteger(quote.storyboard.h3_total_max_ms),
      call_count: optionalInteger(quote.storyboard.call_count),
      total_cap_ms: requireInteger(quote.storyboard.total_cap_ms, 'quote.storyboard.total_cap_ms'),
      frame_ms: optionalInteger(quote.storyboard.frame_ms),
      unit_split: optionalString(quote.storyboard.unit_split),
    },
    is_open: optionalBoolean(quote.is_open),
    first_free: quote.first_free === null || quote.first_free === undefined ? null : quote.first_free,
    current_balance: requireInteger(quote.current_balance, 'quote.current_balance'),
    shortfall_points: requireInteger(quote.shortfall_points, 'quote.shortfall_points'),
  }
}

function parseSubmitResult(value) {
  if (!isObject(value) || !isObject(value.submission)) throw new Error('Story film submission response is invalid')
  return {
    submission: parseSubmission(value.submission),
    task_id: value.task_id === null || value.task_id === undefined ? null : requireInteger(value.task_id, 'task_id'),
    ledger_id: optionalId(value.ledger_id, 'ledger_id'),
    first_free: value.first_free === true,
    replayed: value.replayed === true,
  }
}

function parseTaskList(value) {
  if (!isObject(value) || !Array.isArray(value.tasks)) throw new Error('Story film task list response is invalid')
  return {
    tasks: value.tasks.map(parseTask),
    next_cursor: value.next_cursor === null || value.next_cursor === undefined ? null : String(value.next_cursor),
  }
}

function parseTaskDetail(value) {
  if (!isObject(value) || !isObject(value.submission) || !isObject(value.task)) throw new Error('Story film task detail response is invalid')
  return { submission: parseSubmission(value.submission), task: parseTask(value.task) }
}

function parseSubmission(value) {
  return {
    submission_id: requireInteger(value.submission_id, 'submission_id'),
    episode_id: requireString(value.episode_id, 'episode_id'),
    status: requireString(value.status, 'status'),
    price_points: requireInteger(value.price_points, 'price_points'),
    currency: requireString(value.currency, 'currency'),
    cost_model_version: requireString(value.cost_model_version, 'cost_model_version'),
    trigger_source: requireString(value.trigger_source, 'trigger_source'),
    failure_code: optionalString(value.failure_code),
    refund_ledger_id: optionalId(value.refund_ledger_id, 'refund_ledger_id'),
    created_at: optionalString(value.created_at),
    updated_at: optionalString(value.updated_at),
  }
}

function parseTask(value) {
  return {
    submission_id: requireInteger(value.submission_id, 'submission_id'),
    task_id: requireInteger(value.task_id, 'task_id'),
    episode_id: requireString(value.episode_id, 'episode_id'),
    film_title: optionalString(value.film_title),
    season_no: optionalInteger(value.season_no),
    episode_no: optionalInteger(value.episode_no),
    run_id: optionalString(value.run_id),
    branch_id: optionalString(value.branch_id),
    media_version: optionalString(value.media_version),
    rejected_at: optionalString(value.rejected_at),
    reject_reason: optionalString(value.reject_reason),
    playable: value.playable === null || value.playable === undefined ? null : {
      submission_id: requireInteger(value.playable.submission_id, 'playable.submission_id'),
      media_version: requireString(value.playable.media_version, 'playable.media_version'),
    },
    status: requireString(value.status, 'status'),
    task_status: optionalString(value.task_status),
    provider: optionalString(value.provider),
    attempts: optionalInteger(value.attempts),
    price_points: requireInteger(value.price_points, 'price_points'),
    currency: requireString(value.currency, 'currency'),
    cost_model_version: requireString(value.cost_model_version, 'cost_model_version'),
    trigger_source: requireString(value.trigger_source, 'trigger_source'),
    failure_code: optionalString(value.failure_code),
    refund_ledger_id: optionalId(value.refund_ledger_id, 'refund_ledger_id'),
    created_at: optionalString(value.created_at),
    updated_at: optionalString(value.updated_at),
  }
}

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function requireString(value, field) {
  if (typeof value !== 'string' || value.trim() === '') throw new Error(`Story film production field is invalid: ${field}`)
  return value
}

function requireInteger(value, field) {
  if (value === null || value === undefined || value === '') throw new Error(`Story film production field is invalid: ${field}`)
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 0) throw new Error(`Story film production field is invalid: ${field}`)
  return number
}

function optionalId(value, field) {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && value.trim() !== '') return value
  if (Number.isSafeInteger(Number(value))) return String(value)
  throw new Error(`Story film production field is invalid: ${field}`)
}

function requireNumberString(value, field) {
  const number = requireInteger(value, field)
  return String(number)
}

function optionalBoolean(value) {
  return typeof value === 'boolean' ? value : null
}

function optionalInteger(value) {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (!Number.isSafeInteger(number) || number < 0) return null
  return number
}

function optionalString(value) {
  return value === null || value === undefined ? null : String(value)
}
